import React, { useEffect, useState, useMemo, useCallback } from 'react';

// Hook to handle responsive column count safely
const useMedia = (queries, values, defaultValue) => {
  const match = useCallback(() => {
    // Safety check for SSR or restricted environments
    if (typeof window === 'undefined' || !window.matchMedia) return defaultValue;
    
    try {
      const query = queries.find(q => window.matchMedia(q).matches);
      return query ? values[queries.indexOf(query)] : defaultValue;
    } catch (e) {
      console.warn('MasonryLayout: matchMedia not supported', e);
      return defaultValue;
    }
  }, [queries, values, defaultValue]);

  const [value, setValue] = useState(match);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    
    const handler = () => setValue(match());
    
    // Initial check
    setValue(match());

    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [match]); 

  return value;
};

const MasonryLayout = ({ children }) => {
  // Memoize queries and values to prevent re-running the hook effect on every render
  const queries = useMemo(() => ['(min-width: 1280px)', '(min-width: 1024px)', '(min-width: 768px)'], []);
  const values = useMemo(() => [3, 2, 2], []);
  
  const columns = useMedia(queries, values, 1);

  // Distribute children into columns
  const columnWrapper = useMemo(() => {
    const cols = Array.from({ length: columns }, () => []);
    React.Children.forEach(children, (child, i) => {
      if (child) {
        cols[i % columns].push(child);
      }
    });
    return cols;
  }, [children, columns]);

  return (
    <div className="flex gap-6 w-full items-start">
      {columnWrapper.map((col, columnIndex) => (
        <div key={columnIndex} className="flex flex-col gap-6 flex-1 min-w-0">
          {col.map((item, itemIndex) => (
            <div
              key={item.key || `${columnIndex}-${itemIndex}`}
              className="w-full masonry-item-enter"
              style={{ animationDelay: `${itemIndex * 0.1}s` }}
            >
              {item}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default MasonryLayout;